import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Star, MoreVertical, Eye, Check, X, ChevronDown,
  ThumbsUp, ThumbsDown, Search, Plus, Upload, FileText, Image
} from 'lucide-react';
import { toast } from 'sonner';
import { useUserId } from '../context/UserAuthContext';

const API_URL = window.location.origin;

const ReviewsFeedback = () => {
  const userId = useUserId();
  
  // State
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ approved: 0, requested: 0 });
  const [activeTab, setActiveTab] = useState('Approved');
  const [timeFilter, setTimeFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Add Review Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newReview, setNewReview] = useState({
    reviewer_name: '',
    reviewer_email: '',
    product_name: '',
    rating: 5,
    review_text: '',
    status: 'Requested'
  });
  
  // Upload Modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [parsedReviews, setParsedReviews] = useState(null);
  const fileInputRef = useRef(null);

  // Fetch reviews
  const fetchReviews = useCallback(async () => {
    if (!userId) return;
    try {
      let url = `${API_URL}/api/reviews/?user_id=${userId}&status=${activeTab}`;
      if (timeFilter) {
        url += `&time_filter=${timeFilter}`;
      }
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      
      const res = await fetch(url);
      const data = await res.json();
      setReviews(data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  }, [userId, activeTab, timeFilter, searchQuery]);

  const fetchStats = async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_URL}/api/reviews/stats?user_id=${userId}`);
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Seed data
  const seedData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/reviews/seed?user_id=${userId}`, { method: 'POST' });
      const data = await res.json();
      if (data.seeded) {
        toast.success('Sample reviews loaded');
        fetchReviews();
        fetchStats();
      }
    } catch (error) {
      console.error('Error seeding data:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchReviews(), fetchStats()]);
      setLoading(false);
    };
    
    if (userId) {
      loadData();
    }
  }, [userId, fetchReviews]);

  // Auto-seed if no data
  useEffect(() => {
    if (!loading && reviews.length === 0 && stats.approved === 0 && stats.requested === 0 && userId) {
      seedData();
    }
  }, [loading, reviews.length, stats.approved, stats.requested, userId]);

  // Approve review
  const handleApprove = async (reviewId) => {
    try {
      const res = await fetch(`${API_URL}/api/reviews/${reviewId}/approve`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success('Review approved');
        fetchReviews();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to approve review');
    }
    setActiveMenu(null);
  };

  // Reject review
  const handleReject = async (reviewId) => {
    try {
      const res = await fetch(`${API_URL}/api/reviews/${reviewId}/reject`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success('Review rejected');
        fetchReviews();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to reject review');
    }
    setActiveMenu(null);
  };

  // Add review
  const handleAddReview = async () => {
    if (!newReview.reviewer_name || !newReview.review_text) {
      toast.error('Reviewer name and review text are required');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/reviews/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newReview,
          user_id: userId
        })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Review added');
        setShowAddModal(false);
        setNewReview({
          reviewer_name: '',
          reviewer_email: '',
          product_name: '',
          rating: 5,
          review_text: '',
          status: 'Requested'
        });
        fetchReviews();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to add review');
    }
  };

  // File upload handling
  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf', 'text/csv'];
    const isCSV = file.name.endsWith('.csv');
    
    if (!allowedTypes.includes(file.type) && !isCSV) {
      toast.error('Unsupported file type. Please upload PDF, CSV, JPG, PNG, or WebP files.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    setUploadProgress('reading');
    
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1];
        
        setUploadProgress('parsing');
        
        try {
          const response = await fetch(`${API_URL}/api/reviews/parse-document`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              file_base64: base64,
              file_type: isCSV ? 'text/csv' : file.type,
              file_name: file.name,
              user_id: userId
            })
          });

          const result = await response.json();
          
          if (result.success && result.reviews?.length > 0) {
            setParsedReviews(result.reviews);
            setUploadProgress(null);
            toast.success(`Found ${result.reviews.length} review(s) in document`);
          } else {
            setUploadProgress(null);
            toast.error(result.message || 'No reviews found in document');
          }
        } catch (error) {
          setUploadProgress(null);
          toast.error('Failed to parse document');
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      setUploadProgress(null);
      toast.error('Failed to read file');
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImportParsedReviews = async () => {
    if (!parsedReviews?.length) return;
    
    setUploadProgress('importing');
    
    try {
      const response = await fetch(`${API_URL}/api/reviews/import-parsed?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsedReviews)
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success(`Imported ${result.imported_count} review(s)`);
        setParsedReviews(null);
        setShowUploadModal(false);
        fetchReviews();
        fetchStats();
      } else {
        toast.error('Failed to import reviews');
      }
    } catch (error) {
      toast.error('Failed to import reviews');
    }
    
    setUploadProgress(null);
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setParsedReviews(null);
    setUploadProgress(null);
  };

  // Delete review
  const handleDelete = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      const res = await fetch(`${API_URL}/api/reviews/${reviewId}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        toast.success('Review deleted');
        fetchReviews();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to delete review');
    }
    setActiveMenu(null);
  };

  // View details
  const viewDetails = (review) => {
    setSelectedReview(review);
    setShowDetailModal(true);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Render stars
  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);
    
    return (
      <div className="flex items-center gap-0.5">
        {[...Array(fullStars)].map((_, i) => (
          <Star key={`full-${i}`} size={14} className="text-yellow-400 fill-yellow-400" />
        ))}
        {hasHalf && (
          <Star size={14} className="text-yellow-400 fill-yellow-400 opacity-50" />
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <Star key={`empty-${i}`} size={14} className="text-gray-600" />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading reviews...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-white">Reviews & Feedback</h1>
          
          <div className="flex items-center gap-3">
            {/* Add Review Button */}
            <button
              onClick={() => setShowAddModal(true)}
              data-testid="add-review-btn"
              className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              <Plus size={18} /> Add Review
            </button>
            
            {/* Upload Button */}
            <button
              onClick={() => setShowUploadModal(true)}
              data-testid="upload-reviews-btn"
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors border border-gray-600"
            >
              <Upload size={18} /> Upload
            </button>
            
            {/* Time Filter */}
            <div className="relative">
              <select
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
                className="appearance-none bg-black border border-gray-700 rounded-lg px-4 py-2 pr-10 text-white text-sm focus:outline-none focus:border-gray-600"
              >
                <option value="this_week">This Week</option>
                <option value="this_month">This Month</option>
                <option value="this_year">This Year</option>
                <option value="">All Time</option>
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-6 mb-6 border-b border-gray-800">
          <button
            onClick={() => setActiveTab('Approved')}
            className={`pb-3 px-2 text-sm font-medium transition-colors relative ${
              activeTab === 'Approved' ? 'text-white' : 'text-gray-400'
            }`}
          >
            Approved ({stats.approved || 0})
            {activeTab === 'Approved' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white" />}
          </button>
          <button
            onClick={() => setActiveTab('Requested')}
            className={`pb-3 px-2 text-sm font-medium transition-colors relative ${
              activeTab === 'Requested' ? 'text-white' : 'text-gray-400'
            }`}
          >
            Requested ({stats.requested || 0})
            {activeTab === 'Requested' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white" />}
          </button>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search reviews..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-2 bg-black border border-gray-400 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gray-600"
            />
          </div>
        </div>

        {/* Reviews Table */}
        <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left p-4 text-xs font-medium text-gray-400 uppercase">Review</th>
                <th className="text-left p-4 text-xs font-medium text-gray-400 uppercase">Product</th>
                <th className="text-left p-4 text-xs font-medium text-gray-400 uppercase">Rating</th>
                <th className="text-left p-4 text-xs font-medium text-gray-400 uppercase">Details</th>
                <th className="text-left p-4 text-xs font-medium text-gray-400 uppercase">Action</th>
              </tr>
            </thead>
            <tbody>
              {reviews.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-gray-400">
                    <Star size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No {activeTab.toLowerCase()} reviews found</p>
                  </td>
                </tr>
              ) : (
                reviews.map(review => (
                  <tr key={review.id} className="border-b border-gray-800 hover:bg-gray-900/50">
                    {/* Review Column */}
                    <td className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center text-white font-medium flex-shrink-0">
                          {review.reviewer_avatar || review.reviewer_name?.charAt(0) || 'R'}
                        </div>
                        <div>
                          <p className="text-white font-medium">{review.reviewer_name}</p>
                          <p className="text-gray-500 text-xs">{formatDate(review.created_at)}</p>
                          <p className="text-gray-400 text-sm mt-1 line-clamp-2">{review.review_text}</p>
                        </div>
                      </div>
                    </td>
                    
                    {/* Product Column */}
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center text-2xl">
                          {review.product_image || '📦'}
                        </div>
                        <span className="text-white">{review.product_name}</span>
                      </div>
                    </td>
                    
                    {/* Rating Column */}
                    <td className="p-4">
                      <div>
                        <span className="text-white font-semibold text-lg">{review.rating}</span>
                        <div className="mt-1">
                          {renderStars(review.rating)}
                        </div>
                      </div>
                    </td>
                    
                    {/* Details Column */}
                    <td className="p-4">
                      <button
                        onClick={() => viewDetails(review)}
                        className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm transition-colors"
                      >
                        View Details
                      </button>
                    </td>
                    
                    {/* Action Column */}
                    <td className="p-4">
                      <div className="relative">
                        <button
                          onClick={() => setActiveMenu(activeMenu === review.id ? null : review.id)}
                          className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded"
                        >
                          <MoreVertical size={18} />
                        </button>
                        
                        {activeMenu === review.id && (
                          <div className="absolute right-0 top-10 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-10 min-w-[140px]">
                            <button
                              onClick={() => viewDetails(review)}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-t-lg"
                            >
                              <Eye size={14} /> View
                            </button>
                            {activeTab === 'Requested' && (
                              <>
                                <button
                                  onClick={() => handleApprove(review.id)}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-green-400 hover:bg-gray-800"
                                >
                                  <ThumbsUp size={14} /> Approve
                                </button>
                                <button
                                  onClick={() => handleReject(review.id)}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-gray-800"
                                >
                                  <ThumbsDown size={14} /> Reject
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleDelete(review.id)}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-gray-800 rounded-b-lg"
                            >
                              <X size={14} /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Detail Modal */}
        {showDetailModal && selectedReview && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg">
              <div className="p-6 border-b border-gray-700 flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">Review Details</h2>
                <button onClick={() => { setShowDetailModal(false); setSelectedReview(null); }} className="text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Reviewer Info */}
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gray-700 rounded-full flex items-center justify-center text-white text-xl font-medium">
                    {selectedReview.reviewer_avatar || selectedReview.reviewer_name?.charAt(0) || 'R'}
                  </div>
                  <div>
                    <p className="text-white font-semibold text-lg">{selectedReview.reviewer_name}</p>
                    <p className="text-gray-500 text-sm">{formatDate(selectedReview.created_at)}</p>
                  </div>
                </div>
                
                {/* Product */}
                <div className="flex items-center gap-4 p-4 bg-black rounded-lg">
                  <div className="w-16 h-16 bg-gray-800 rounded-lg flex items-center justify-center text-3xl">
                    {selectedReview.product_image || '📦'}
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Product</p>
                    <p className="text-white font-medium">{selectedReview.product_name}</p>
                  </div>
                </div>
                
                {/* Rating */}
                <div className="p-4 bg-black rounded-lg">
                  <p className="text-gray-400 text-sm mb-2">Rating</p>
                  <div className="flex items-center gap-3">
                    <span className="text-3xl font-bold text-white">{selectedReview.rating}</span>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map(star => (
                        <Star 
                          key={star}
                          size={24}
                          className={star <= selectedReview.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Review Text */}
                <div className="p-4 bg-black rounded-lg">
                  <p className="text-gray-400 text-sm mb-2">Review</p>
                  <p className="text-white">{selectedReview.review_text}</p>
                </div>
                
                {/* Status */}
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 text-sm">Status:</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    selectedReview.status === 'Approved' ? 'bg-green-500/20 text-green-400' :
                    selectedReview.status === 'Requested' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {selectedReview.status}
                  </span>
                </div>
                
                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  {selectedReview.status === 'Requested' && (
                    <>
                      <button
                        onClick={() => { handleApprove(selectedReview.id); setShowDetailModal(false); }}
                        className="flex-1 flex items-center justify-center gap-2 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
                      >
                        <ThumbsUp size={18} /> Approve
                      </button>
                      <button
                        onClick={() => { handleReject(selectedReview.id); setShowDetailModal(false); }}
                        className="flex-1 flex items-center justify-center gap-2 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium"
                      >
                        <ThumbsDown size={18} /> Reject
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => { setShowDetailModal(false); setSelectedReview(null); }}
                    className={`${selectedReview.status === 'Requested' ? 'px-6' : 'flex-1'} py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg`}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Click outside to close menu */}
      {activeMenu && (
        <div className="fixed inset-0 z-0" onClick={() => setActiveMenu(null)} />
      )}
      
      {/* Add Review Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg" data-testid="add-review-modal">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Add New Review</h2>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Reviewer Name *</label>
                <input
                  type="text"
                  value={newReview.reviewer_name}
                  onChange={(e) => setNewReview({...newReview, reviewer_name: e.target.value})}
                  placeholder="Enter reviewer name"
                  data-testid="reviewer-name-input"
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gray-500"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Reviewer Email</label>
                <input
                  type="email"
                  value={newReview.reviewer_email}
                  onChange={(e) => setNewReview({...newReview, reviewer_email: e.target.value})}
                  placeholder="Enter email (optional)"
                  data-testid="reviewer-email-input"
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gray-500"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Product Name</label>
                <input
                  type="text"
                  value={newReview.product_name}
                  onChange={(e) => setNewReview({...newReview, product_name: e.target.value})}
                  placeholder="Enter product name"
                  data-testid="product-name-input"
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gray-500"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Rating</label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setNewReview({...newReview, rating: star})}
                      data-testid={`rating-star-${star}`}
                      className="focus:outline-none"
                    >
                      <Star 
                        size={28}
                        className={star <= newReview.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600 hover:text-yellow-400'}
                      />
                    </button>
                  ))}
                  <span className="ml-2 text-white font-medium">{newReview.rating}/5</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Review Text *</label>
                <textarea
                  value={newReview.review_text}
                  onChange={(e) => setNewReview({...newReview, review_text: e.target.value})}
                  placeholder="Enter review content"
                  rows={4}
                  data-testid="review-text-input"
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gray-500 resize-none"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Status</label>
                <select
                  value={newReview.status}
                  onChange={(e) => setNewReview({...newReview, status: e.target.value})}
                  data-testid="review-status-select"
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gray-500"
                >
                  <option value="Requested">Requested</option>
                  <option value="Approved">Approved</option>
                </select>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleAddReview}
                  data-testid="submit-review-btn"
                  className="flex-1 py-3 bg-white text-black rounded-lg font-medium hover:bg-gray-200 transition-colors"
                >
                  Add Review
                </button>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg" data-testid="upload-reviews-modal">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Upload Reviews</h2>
              <button onClick={handleCloseUploadModal} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6">
              {/* Upload Area */}
              {!parsedReviews && (
                <div className="space-y-4">
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-600 rounded-xl p-8 text-center cursor-pointer hover:border-gray-500 transition-colors"
                  >
                    {uploadProgress ? (
                      <div className="space-y-3">
                        <div className="w-12 h-12 mx-auto border-4 border-gray-600 border-t-white rounded-full animate-spin" />
                        <p className="text-gray-400">
                          {uploadProgress === 'reading' && 'Reading file...'}
                          {uploadProgress === 'parsing' && 'Parsing document with AI...'}
                        </p>
                      </div>
                    ) : (
                      <>
                        <Upload size={48} className="mx-auto text-gray-500 mb-4" />
                        <p className="text-white font-medium mb-2">Click to upload or drag and drop</p>
                        <p className="text-gray-500 text-sm">Supports PDF, CSV, JPG, PNG, WebP (Max 10MB)</p>
                      </>
                    )}
                  </div>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.csv,.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp,application/pdf,text/csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-upload-input"
                  />
                  
                  <div className="flex items-start gap-3 p-4 bg-black rounded-lg">
                    <FileText size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-white text-sm font-medium">Supported Formats</p>
                      <p className="text-gray-500 text-xs mt-1">
                        CSV files with columns: reviewer_name, product_name, rating, review_text<br/>
                        PDF documents, screenshots of reviews, or images with review data
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Parsed Results */}
              {parsedReviews && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-green-400 mb-4">
                    <Check size={20} />
                    <span>Found {parsedReviews.length} review(s)</span>
                  </div>
                  
                  <div className="max-h-64 overflow-y-auto space-y-3">
                    {parsedReviews.map((review, idx) => (
                      <div key={idx} className="p-4 bg-black rounded-lg border border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white font-medium">{review.reviewer_name || 'Anonymous'}</span>
                          <div className="flex items-center gap-1">
                            <Star size={14} className="text-yellow-400 fill-yellow-400" />
                            <span className="text-white text-sm">{review.rating || 5}</span>
                          </div>
                        </div>
                        <p className="text-gray-400 text-sm">{review.product_name}</p>
                        <p className="text-gray-300 text-sm mt-2 line-clamp-2">{review.review_text}</p>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={handleImportParsedReviews}
                      disabled={uploadProgress === 'importing'}
                      data-testid="import-reviews-btn"
                      className="flex-1 py-3 bg-white text-black rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                      {uploadProgress === 'importing' ? 'Importing...' : `Import ${parsedReviews.length} Review(s)`}
                    </button>
                    <button
                      onClick={() => setParsedReviews(null)}
                      className="px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewsFeedback;
